import { cn } from "@/lib/utils";

export interface NodeType {
  type: string;
  label: string;
  icon: string;
  category: "trigger" | "condition" | "action";
  description: string;
}

// ---------------------------------------------------------------------------
// WF-006 — Trigger palette covers the 14 F-046 backend trigger sources proven
// by packages/workflows/src/__tests__/wf-006-source-event-matrix.test.ts.
// Palette parity is the remaining gap that keeps WF-006 PARTIAL; backend
// trigger fanout (TriggerEvaluator) already accepts these source events, but
// the editor only exposed 4 of them — authors had no way to wire the rest.
// ---------------------------------------------------------------------------
export const NODE_TYPES: NodeType[] = [
  // Triggers (F-046 source-event matrix)
  { type: "trigger_manual", label: "Manual Trigger", icon: "👆", category: "trigger", description: "Triggered manually by a user" },
  { type: "trigger_event", label: "Event Trigger", icon: "⚡", category: "trigger", description: "Fires when a specific event occurs" },
  { type: "trigger_scheduled", label: "Scheduled", icon: "🕐", category: "trigger", description: "Runs on a schedule (cron)" },
  { type: "trigger_form", label: "Form Submitted", icon: "📝", category: "trigger", description: "When a form is submitted" },
  { type: "trigger_booking_created", label: "Booking Created", icon: "📅", category: "trigger", description: "When a booking is created" },
  { type: "trigger_booking_cancelled", label: "Booking Cancelled", icon: "🚫", category: "trigger", description: "When a booking is cancelled" },
  { type: "trigger_deal_stage_changed", label: "Deal Stage Changed", icon: "📈", category: "trigger", description: "When a deal moves between pipeline stages" },
  { type: "trigger_email_opened", label: "Email Opened", icon: "📬", category: "trigger", description: "When a contact opens an email" },
  { type: "trigger_email_clicked", label: "Email Clicked", icon: "🖱️", category: "trigger", description: "When a contact clicks a link in an email" },
  { type: "trigger_field_changed", label: "Field Changed", icon: "✏️", category: "trigger", description: "When a contact/deal field value changes" },
  { type: "trigger_segment_entered", label: "Segment Entered", icon: "👥", category: "trigger", description: "When a contact enters a segment" },
  { type: "trigger_segment_exited", label: "Segment Exited", icon: "🚪", category: "trigger", description: "When a contact exits a segment" },
  { type: "trigger_webhook", label: "Webhook Received", icon: "🪝", category: "trigger", description: "When an external webhook is received" },
  { type: "trigger_page_visit", label: "Page Visit", icon: "🌐", category: "trigger", description: "When a contact visits a tracked page" },
  { type: "trigger_payment_completed", label: "Payment Completed", icon: "💳", category: "trigger", description: "When a payment is completed" },
  { type: "trigger_custom_event", label: "Custom Event", icon: "🎯", category: "trigger", description: "When a custom event is emitted" },
  // Conditions
  { type: "condition_if", label: "If / Else", icon: "🔀", category: "condition", description: "Branch based on field conditions" },
  { type: "condition_wait", label: "Wait / Delay", icon: "⏳", category: "condition", description: "Wait for a duration or until a condition" },
  { type: "condition_split", label: "A/B Split", icon: "📊", category: "condition", description: "Split traffic for testing" },
  // Actions
  { type: "action_send_email", label: "Send Email", icon: "📧", category: "action", description: "Send an email to the contact" },
  { type: "action_send_sms", label: "Send SMS", icon: "💬", category: "action", description: "Send an SMS message" },
  { type: "action_webhook", label: "Call Webhook", icon: "🔗", category: "action", description: "Send data to an external URL" },
  { type: "action_update_crm", label: "Update CRM", icon: "👤", category: "action", description: "Update a contact or deal field" },
  { type: "action_add_tag", label: "Add Tag", icon: "🏷️", category: "action", description: "Add a tag to the contact" },
  { type: "action_create_task", label: "Create Task", icon: "✅", category: "action", description: "Create a CRM task" },
];

const CATEGORIES = [
  { key: "trigger" as const, label: "Triggers", color: "text-[var(--color-secondary)]" },
  { key: "condition" as const, label: "Conditions", color: "text-yellow-600" },
  { key: "action" as const, label: "Actions", color: "text-[var(--color-primary)]" },
];

interface NodePaletteProps {
  onAddNode: (type: string) => void;
}

/** Sidebar palette of draggable node types for the workflow builder. */
export function NodePalette({ onAddNode }: NodePaletteProps) {
  return (
    <div className="w-56 shrink-0 overflow-y-auto border-r border-border bg-muted/30 p-3">
      <p className="mb-3 text-xs font-medium uppercase text-muted-foreground">Add Node</p>
      {CATEGORIES.map((cat) => (
        <div key={cat.key} className="mb-4">
          <p className={cn("mb-1.5 text-xs font-semibold", cat.color)}>{cat.label}</p>
          <div className="space-y-1">
            {NODE_TYPES.filter((n) => n.category === cat.key).map((node) => (
              <button
                key={node.type}
                onClick={() => onAddNode(node.type)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-accent transition-colors"
                title={node.description}
              >
                <span>{node.icon}</span>
                <span>{node.label}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
